import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputBase from '@material-ui/core/InputBase';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/styles';
import {
  getCountryCallingCode,
  isSupportedCountry,
  parseIncompletePhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { countryCallCodes, countryMessages } from '../../helpers/countries';
import PropTypes, { intlPropType } from '../../helpers/propTypes';
import useId from '../../hooks/useId';
import { userMetrics } from '../../userMetrics';
import messages from './messages';

const useStyles = makeStyles((theme) => ({
  flagButton: {
    height: '100%',
    minWidth: 0,
    display: 'block',
    backgroundSize: '50%',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'white',
    backgroundPosition: '20% center',
    width: '90px',
    outline: 'none',
    border: 'none',
    borderRight: `1px solid ${theme.palette.grey[350]}`,
    position: 'relative',
    overflow: 'hidden',
    marginBottom: 0,
  },

  select: {
    width: '100%',
    height: '100%',
    padding: '0 100%',
  },
}));

function FormPhoneInput({
  intl: { formatMessage },
  label,
  name,
  placeholder,
  error,
  value,
  type,
  onChange,
  onBlur,
  onFocus = () => {},
  additionalProps: { country },
  required,
}) {
  const [selectedCountry, setCountry] = useState(country.toUpperCase());
  const [currentValue, setCurrentInputValue] = useState('');
  const setCurrentValue = (newValue) => {
    setCurrentInputValue(newValue);
    let number;
    try {
      number = parsePhoneNumberFromString(newValue, selectedCountry).format(
        'E.164',
      );
    } catch (e) {
      number = parseIncompletePhoneNumber(newValue);
    }
    onChange({
      name,
      type,
      value: number,
    });
  };

  const labelId = useId();
  const inputId = useId();

  const classes = useStyles();

  const select = useMemo(
    () => (
      <Select
        className={classes.flagButton}
        data-testid="phone-country-select"
        inputProps={{
          'aria-label': formatMessage(messages.phoneCountryOfOrigin),
          className: classes.select,
        }}
        native
        onChange={(e) => setCountry(e.target.value)}
        onFocus={() =>
          userMetrics.track('Contact info select focus phone prefix')
        }
        style={{
          backgroundImage: `url(https://public.trialbee.com/flags/${selectedCountry.toLowerCase()}.svg)`,
        }}
        value={selectedCountry}
      >
        {Object.entries(countryCallCodes).map(([countryCode, callCode]) => {
          const countryMessage = countryMessages[countryCode];

          return (
            <option key={countryCode} value={countryCode}>
              {formatMessage(countryMessage)} (+{callCode})
            </option>
          );
        })}
      </Select>
    ),
    [classes, selectedCountry, setCountry, countryCallCodes],
  );

  function updateCountry(newValue) {
    const newNumber = parseIncompletePhoneNumber(newValue).substring(0, 15);
    if (isSupportedCountry(selectedCountry)) {
      if (newNumber.startsWith('+')) {
        try {
          const phoneNumber = parsePhoneNumberFromString(
            newNumber,
            selectedCountry,
          );
          if (phoneNumber.country) {
            setCountry(phoneNumber.country);
          }
        } catch (e) {}
      }
    }

    return newValue;
  }

  function handleChange(e) {
    updateCountry(e.target.value);
    setCurrentValue(e.target.value);
  }

  // TODO: This only picks up changes from above if the input is currently empty which isn't
  // optimal but we currently don't have a situation where it affects us too much.
  useEffect(() => {
    if (!currentValue) {
      const phoneNumber = parsePhoneNumberFromString(value);
      if (!phoneNumber) return;
      if (phoneNumber.country) setCountry(phoneNumber.country);
      setCurrentInputValue(phoneNumber.formatNational());
    }
  }, [value]);

  useEffect(() => {
    if (!currentValue) return;
    let newValue = currentValue;
    const number = parsePhoneNumberFromString(currentValue);
    if (number) {
      newValue = `+${getCountryCallingCode(selectedCountry)}${
        number.nationalNumber
      }`;
    }
    setCurrentInputValue(newValue);
  }, [selectedCountry]);

  return (
    <FormControl
      aria-labelledby={labelId}
      data-testid="phone-input"
      error={Boolean(error)}
      fullWidth
      required={required}
      role="group"
    >
      <InputLabel htmlFor={inputId} id={labelId} shrink>
        {label}
      </InputLabel>
      <InputBase
        autoComplete="off"
        id={inputId}
        inputProps={{
          'aria-describedby': error ? `${inputId}-helper-text` : '',
        }}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        placeholder={placeholder}
        startAdornment={select}
        type="tel"
        value={currentValue}
      />
      {error !== undefined && (
        <FormHelperText id={`${inputId}-helper-text`}>{error}</FormHelperText>
      )}
    </FormControl>
  );
}

FormPhoneInput.propTypes = {
  additionalProps: PropTypes.shape({ country: PropTypes.string.isRequired })
    .isRequired,
  error: PropTypes.string,
  intl: intlPropType.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool,
  type: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

FormPhoneInput.defaultProps = {
  error: undefined,
  onFocus: () => {},
  required: false,
};

export default injectIntl(FormPhoneInput);
